import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { ArrowLeft, Calendar, Plane, GraduationCap, Check } from "lucide-react";

const SCHOOL_YEAR_START = 2026;
const SCHOOL_YEAR_END = 2027;
const SEPTEMBER = 8;
const DECEMBER = 11;
const AUGUST = 7;

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const HOLIDAY_META = {
  rentree: { icon: GraduationCap, color: "var(--ok)", label: "Rentrée" },
  vacances: { icon: Plane, color: "var(--maroon)", label: "Vacances" },
  ferie: { icon: Calendar, color: "var(--just)", label: "Férié" },
};

function parseISO(d) {
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day);
}

function fmtFr(d) {
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function buildMonthDays(year, monthIndex) {
  // Returns array of { date, inMonth } for the calendar grid
  const first = new Date(year, monthIndex, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const days = [];
  for (let i = 0; i < startOffset; i++) {
    days.push({ date: new Date(year, monthIndex, i - startOffset + 1), inMonth: false });
  }
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  for (let i = 1; i <= lastDay; i++) {
    days.push({ date: new Date(year, monthIndex, i), inMonth: true });
  }
  while (days.length % 7 !== 0) {
    const d = days[days.length - 1].date;
    days.push({ date: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1), inMonth: false });
  }
  return days;
}

function dayHolidayType(date, holidays) {
  for (const h of holidays) {
    const s = parseISO(h.start);
    const e = parseISO(h.end);
    if (date >= s && date <= e) return { type: h.type, name: h.name };
  }
  return null;
}

export default function Planning() {
  const [calendar, setCalendar] = useState(null);

  useEffect(() => {
    api.get("/calendar").then(({ data }) => setCalendar(data));
  }, []);

  if (!calendar) {
    return (
      <div className="min-h-screen bg-[var(--cream)]">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 text-[var(--ink-soft)]">Chargement...</div>
      </div>
    );
  }

  const months = [];
  // Année scolaire : septembre SCHOOL_YEAR_START → août SCHOOL_YEAR_END
  for (let m = SEPTEMBER; m <= DECEMBER; m++) months.push({ year: SCHOOL_YEAR_START, m });
  for (let m = 0; m <= AUGUST; m++) months.push({ year: SCHOOL_YEAR_END, m });

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/teacher" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="planning-back">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-6 pb-8 border-b border-[var(--parchment-2)]">
          <div>
            <div className="eyebrow">Planning général · Zone B</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Année scolaire 2026 — 2027</h1>
            <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
              Calendrier officiel (Zone B) avec les vacances et jours fériés. Les jours grisés sont des
              week-ends et jours fériés ; les périodes colorées correspondent aux vacances scolaires.
            </p>
          </div>
          <div className="flex flex-wrap gap-6 text-xs">
            <LegendItem color="var(--maroon)" label="Vacances scolaires" />
            <LegendItem color="var(--just)" label="Jour férié" />
            <LegendItem color="var(--ok)" label="Rentrée" />
            <LegendItem color="var(--parchment)" label="Week-end" border />
            <span className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-3 h-3 line-through text-[var(--ink-soft)]">·</span>
              <span className="text-[var(--ink-soft)]">Jour passé</span>
            </span>
          </div>
        </div>

        {/* Holidays list */}
        <section className="mt-10">
          <h2 className="font-serif-display text-2xl font-semibold mb-4">Vacances officielles</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="holidays-list">
            {calendar.holidays.map((h, i) => {
              const { icon: Icon, color, label } = HOLIDAY_META[h.type] || HOLIDAY_META.ferie;
              return (
                <div key={`${h.name}-${h.start}`} className="bg-white border border-[var(--parchment-2)] p-4" data-testid={`holiday-${i}`}>
                  <div className="flex items-center gap-2">
                    <Icon size={16} style={{ color }} />
                    <span className="eyebrow !text-[var(--ink-soft)]">{label}</span>
                  </div>
                  <div className="mt-2 font-serif-display text-lg font-semibold">{h.name}</div>
                  <div className="mt-1 text-sm text-[var(--ink-soft)] font-mono-spec">
                    {h.start === h.end ? fmtFr(parseISO(h.start)) : `${fmtFr(parseISO(h.start))} → ${fmtFr(parseISO(h.end))}`}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Months grid */}
        <section className="mt-12">
          <h2 className="font-serif-display text-2xl font-semibold mb-4">Calendrier mois par mois</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {months.map(({ year, m }) => (
              <MonthGrid key={`${year}-${m}`} year={year} monthIndex={m} holidays={calendar.holidays} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function LegendItem({ color, label, border }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block w-3 h-3"
        style={{ background: color, border: border ? "1px solid var(--parchment-2)" : "none" }}
      />
      <span className="text-[var(--ink-soft)]">{label}</span>
    </div>
  );
}

function MonthGrid({ year, monthIndex, holidays }) {
  const days = buildMonthDays(year, monthIndex);
  const dow = ["L", "M", "M", "J", "V", "S", "D"];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    <div className="bg-white border border-[var(--parchment-2)] p-4" data-testid={`month-${year}-${monthIndex}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-serif-display text-lg font-semibold">{MONTHS_FR[monthIndex]}</div>
        <div className="eyebrow">{year}</div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] eyebrow !text-[var(--muted)] mb-1">
        {dow.map((d, i) => <div key={`${d}-${i}`}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const inMonth = d.inMonth;
          const isWeekend = d.date.getDay() === 0 || d.date.getDay() === 6;
          const h = dayHolidayType(d.date, holidays);
          const isPast = inMonth && d.date < today;
          const isToday = inMonth && d.date.getTime() === today.getTime();
          let cls = "h-7 flex items-center justify-center text-xs relative ";
          if (!inMonth) {
            cls += "text-[var(--parchment-2)]";
          } else if (h?.type === "vacances") {
            cls += "bg-[var(--maroon)] text-white";
          } else if (h?.type === "rentree") {
            cls += "bg-[var(--ok)] text-white";
          } else if (h?.type === "ferie") {
            cls += "bg-[var(--just)] text-white";
          } else if (isWeekend) {
            cls += "text-[var(--muted)] bg-[var(--parchment)]";
          } else {
            cls += "text-[var(--ink)]";
          }
          if (isToday) cls += " ring-2 ring-[var(--maroon)] ring-offset-1 font-semibold";
          if (isPast && !h) cls += " line-through opacity-60";
          return (
            <div key={d.date.toISOString()} className={cls} title={h?.name || (isPast ? "Jour passé" : "")} data-testid={isToday ? "today-cell" : undefined}>
              {d.date.getDate()}
              {isPast && !h && !isWeekend && (
                <Check size={8} className="absolute bottom-0 right-0.5 text-[var(--ok)]" strokeWidth={3} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
